import { useMemo } from 'react';
import type { Conversation } from '../../stores/whatsapp/chatStore';

// Categoriza uma conversa pelo assunto + ultima mensagem. Mesmas regras usadas
// no kanbanStore.detectCategory, centralizadas aqui para as telas de analise
// nao duplicarem a logica.
export function detectCategoria(subject?: string, lastMessage?: string): string {
  const text = `${subject || ''} ${lastMessage || ''}`.toLowerCase();
  if (
    text.includes('irpf') ||
    text.includes('imposto') ||
    text.includes('fiscal') ||
    text.includes('nota')
  )
    return 'fiscal';
  if (
    text.includes('folha') ||
    text.includes('ferias') ||
    text.includes('rescisao') ||
    text.includes('pessoal')
  )
    return 'pessoal';
  if (text.includes('balancete') || text.includes('contabil') || text.includes('dre'))
    return 'contabil';
  if (
    text.includes('abertura') ||
    text.includes('societario') ||
    text.includes('holding') ||
    text.includes('contrato')
  )
    return 'societario';
  return 'consultoria';
}

export interface WaLocalInsights {
  sentimentos: { positivo: number; neutro: number; negativo: number };
  topicos: { nome: string; quantidade: number }[];
  /** Quantas conversas do periodo realmente avaliaram (rating > 0) */
  totalAvaliadas: number;
  /** Quantas conversas do periodo estao encerradas */
  totalFinalizados: number;
}

/**
 * Insights confiaveis calculados localmente a partir das conversas (ja
 * filtradas por periodo pela tela). Sem IA:
 * - Sentimento: derivado das avaliacoes reais (rating 1-5) — >=4 positivo,
 *   ==3 neutro, <=2 negativo, em % sobre quem avaliou. Se ninguem avaliou,
 *   retorna 0/0/0 (honesto, em vez de numero inventado).
 * - Topicos: categoriza cada conversa pelo assunto/ultima mensagem.
 *
 * E a base estavel das telas de analise; a IA so complementa com recomendacoes.
 */
export function useWaLocalInsights(conversations: Conversation[]): WaLocalInsights {
  return useMemo(() => {
    // Topicos por categoria detectada
    const catMap: Record<string, number> = {};
    conversations.forEach(c => {
      const cat = detectCategoria(c.subject, c.last_message);
      catMap[cat] = (catMap[cat] || 0) + 1;
    });
    const topicos = Object.entries(catMap)
      .map(([nome, quantidade]) => ({ nome, quantidade }))
      .sort((a, b) => b.quantidade - a.quantidade);

    // Sentimento real pelas avaliacoes (rating 1-5)
    const avaliadas = conversations.filter(c => typeof c.rating === 'number' && c.rating > 0);
    const base = avaliadas.length || 1;
    const positivas = avaliadas.filter(c => (c.rating as number) >= 4).length;
    const neutras = avaliadas.filter(c => (c.rating as number) === 3).length;
    const negativas = avaliadas.filter(c => (c.rating as number) <= 2).length;

    return {
      sentimentos: {
        positivo: avaliadas.length > 0 ? Math.round((positivas / base) * 100) : 0,
        neutro: avaliadas.length > 0 ? Math.round((neutras / base) * 100) : 0,
        negativo: avaliadas.length > 0 ? Math.round((negativas / base) * 100) : 0,
      },
      topicos,
      totalAvaliadas: avaliadas.length,
      totalFinalizados: conversations.filter(c => c.status === 'closed').length,
    };
  }, [conversations]);
}
